import { useEffect, useRef } from 'react';

const specialties = [
  { name: 'Psychiatry', conditions: 7, prevalence: '58+ million', priority: 'MDD, Bipolar, Anxiety', color: 'primary' },
  { name: 'Neurology', conditions: 8, prevalence: '20+ million', priority: 'Alzheimer\'s, Parkinson\'s, ALS, MS', color: 'accent' },
  { name: 'Pulmonology', conditions: 6, prevalence: '71+ million', priority: 'COPD, Asthma, Sleep Apnea', color: 'secondary' },
  { name: 'Cardiology', conditions: 5, prevalence: '151+ million', priority: 'Heart Failure, CAD, Pulm HTN', color: 'primary' },
  { name: 'Endocrinology', conditions: 4, prevalence: '40+ million', priority: 'Diabetes, Thyroid disorders', color: 'accent' },
  { name: 'ENT', conditions: 4, prevalence: 'Common', priority: 'Laryngeal cancer, Vocal disorders', color: 'secondary' },
  { name: 'Infectious Disease', conditions: 3, prevalence: 'Episodic', priority: 'COVID-19, URI', color: 'primary' },
  { name: 'Oncology', conditions: 2, prevalence: '156K/year', priority: 'Brain tumors, Head & neck', color: 'accent' },
  { name: 'Pediatrics', conditions: 2, prevalence: 'Common', priority: 'Speech disorders, Apraxia', color: 'secondary' },
  { name: 'Geriatrics', conditions: 2, prevalence: 'Most elderly', priority: 'Frailty, Presbyphonia', color: 'primary' },
  { name: 'Rheumatology', conditions: 2, prevalence: '2-5 million', priority: 'RA, Sjogren\'s', color: 'accent' },
  { name: 'Substance Use', conditions: 2, prevalence: '15+ million', priority: 'Alcohol, Opioid', color: 'secondary' },
  { name: 'Sleep Medicine', conditions: 1, prevalence: '50-70 million', priority: 'Insomnia', color: 'primary' },
  { name: 'Pain Medicine', conditions: 1, prevalence: '50 million', priority: 'Chronic pain', color: 'accent' },
];

export default function SpecialtyPotentialSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full bg-background-100 py-20 md:py-32 px-6 md:px-12 lg:px-20"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <div className="reveal opacity-0 mb-4">
            <span className="font-body text-xs font-semibold tracking-widest text-primary-500 uppercase">
              Clinical Applications
            </span>
          </div>
          <h2 className="reveal opacity-0 font-display text-3xl md:text-4xl lg:text-5xl font-medium text-foreground-50 leading-tight mb-4">
            Vocal Biomarker Potential by Specialty
          </h2>
          <p className="reveal opacity-0 font-body text-base text-foreground-400 max-w-2xl mx-auto">
            Our voice AI technology spans across 14 medical specialties, covering over 400 million potential patients worldwide.
          </p>
        </div>

        {/* Grid Cards */}
        <div className="reveal opacity-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {specialties.map((spec, i) => {
            const colorClass = spec.color === 'primary'
              ? 'bg-primary-500/10 border-primary-500/20 hover:border-primary-500/40'
              : spec.color === 'accent'
              ? 'bg-accent-500/10 border-accent-500/20 hover:border-accent-500/40'
              : 'bg-secondary-500/10 border-secondary-500/20 hover:border-secondary-500/40';

            const dotColor = spec.color === 'primary'
              ? 'bg-primary-500'
              : spec.color === 'accent'
              ? 'bg-accent-500'
              : 'bg-secondary-500';

            const textColor = spec.color === 'primary'
              ? 'text-primary-500'
              : spec.color === 'accent'
              ? 'text-accent-500'
              : 'text-secondary-500';

            return (
              <div
                key={i}
                className={`p-5 rounded-xl border ${colorClass} transition-all duration-500 ease-silk group`}
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-2 h-2 rounded-full ${dotColor}`} />
                  <h3 className="font-body text-sm font-semibold text-foreground-50">
                    {spec.name}
                  </h3>
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className={`font-display text-2xl font-medium ${textColor}`}>
                    {spec.conditions}
                  </span>
                  <span className="font-body text-xs text-foreground-600">
                    conditions
                  </span>
                </div>
                <div className="font-body text-xs text-foreground-400 mb-2">
                  {spec.prevalence} affected
                </div>
                <div className="font-body text-xs text-foreground-500 leading-relaxed">
                  {spec.priority}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}